import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Lock, Pencil } from "lucide-react";
import {
  AddressFields,
  EMPTY_ADDRESS,
  toAddress,
  type AddressValue,
} from "@/components/onboarding/AddressFields";
import { addressLines, hasSeparateShipping, shippingAddressOf } from "@/lib/shippingDestination";

const STAGE_ORDER = ["pending", "ready", "ein_acquired", "filings_submitted", "filings_received", "docs_sent", "docs_delivered"];
const LOCKABLE = [
  "legal_full_name",
  "una_name",
  "suite_number",
  "legal_address",
  "shipping_address",
];

const STAGE_LABEL: Record<string, string> = {
  pending: "Getting started",
  ready: "Ready for formation",
  ein_acquired: "EIN in hand",
  filings_submitted: "Filed with the state",
  filings_received: "State has processed",
  docs_sent: "Documents shipped",
  docs_delivered: "Documents delivered",
};

const HOME_LABEL: Record<string, string> = {
  solo: "It's just me.",
  us: "It's just us.",
  us_kids_grown: "It's just us + kid(s) are grown.",
  me_kids_grown: "It's just me + kid(s) are grown.",
  us_kids: "It's us + kid(s).",
  me_kids: "It's me + kid(s).",

};

const FINANCE_LABEL: Record<string, string> = {
  seg_solo: "I handle it all myself.",
  seg_seasonal: "We see a tax professional about once a year.",
  seg_counseled: "I have standing relationships with a CPA or attorney.",
  seg_house: "We have established planning in place.",
};

type Field = {
  key: string;
  label: string;
  value: string;
  editable: boolean;
  kind?: "text" | "address";
};

function addressText(a: any): string {
  if (!a) return "";
  return [a.line1, a.line2, [a.city, a.state].filter(Boolean).join(", "), a.zip]
    .filter(Boolean)
    .join("\n");
}

export default function MemberRecord() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState<any>(null);
  const [editField, setEditField] = useState<Field | null>(null);
  const [editValue, setEditValue] = useState("");
  const [fixField, setFixField] = useState<Field | null>(null);
  const [fixValue, setFixValue] = useState("");
  const [fixNote, setFixNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [addrField, setAddrField] = useState<Field | null>(null);
  const [addrValue, setAddrValue] = useState<AddressValue>(EMPTY_ADDRESS);

  const load = useCallback(async () => {
    if (!user) return;
    const { data } = await (supabase as any)
      .from("member_records")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();
    setRecord(data);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  const locked = record
    ? STAGE_ORDER.indexOf(record.fulfillment_stage) > STAGE_ORDER.indexOf("ready")
    : false;

  const field = (
    key: string,
    label: string,
    value: any,
    editable = true,
    kind: "text" | "address" = "text",
  ): Field => ({
    key,
    label,
    value: value === null || value === undefined ? "" : String(value),
    editable: editable && !(locked && LOCKABLE.includes(key)),
    kind,
  });

  const openAddress = (f: Field) => {
    const raw = record?.[f.key];
    setAddrValue(toAddress(raw));
    setAddrField(f);
  };

  const saveAddress = async () => {
    if (!addrField || !record) return;
    setSaving(true);
    const payload: Record<string, unknown> = { ...addrValue };
    const { error } = await (supabase as any)
      .from("member_records")
      .update({ [addrField.key]: payload })
      .eq("id", record.id);
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Saved");
    setAddrField(null);
    void load();
  };

  const saveEdit = async () => {
    if (!editField || !record) return;
    setSaving(true);
    const { error } = await (supabase as any)
      .from("member_records")
      .update({ [editField.key]: editValue })
      .eq("id", record.id);
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Saved");
    setEditField(null);
    void load();
  };

  const submitFix = async () => {
    if (!fixField || !record) return;
    setSaving(true);
    const { error } = await (supabase as any).from("record_correction_requests").insert({
      member_record_id: record.id,
      field_key: fixField.key,
      current_value: fixField.value,
      requested_value: fixValue,
      member_note: fixNote,
    });
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Thank you. Our team will take it from here.");
    setFixField(null);
    setFixValue("");
    setFixNote("");
  };

  const Row = ({ f }: { f: Field }) => (
    <div className="flex items-start justify-between gap-3 border-b py-3 last:border-b-0">
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground">{f.label}</div>
        <div className="whitespace-pre-line break-words text-sm">{f.value || "Not set"}</div>
      </div>
      {f.editable ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            if (f.kind === "address") {
              openAddress(f);
              return;
            }
            setEditField(f);
            setEditValue(f.value);
          }}
        >
          <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
        </Button>
      ) : (
        <Button
          size="sm"
          variant="ghost"
          className="text-muted-foreground"
          onClick={() => {
            setFixField(f);
            setFixValue(f.value);
            setFixNote("");
          }}
        >
          <Lock className="h-3.5 w-3.5 mr-1" /> Request a correction
        </Button>
      )}
    </div>
  );

  if (loading) {
    return (
      <MainLayout>
        <div className="container max-w-3xl mx-auto px-4 py-12 flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading your record
        </div>
      </MainLayout>
    );
  }

  if (!record) {
    return (
      <MainLayout>
        <div className="container max-w-3xl mx-auto px-4 py-12">
          <p className="text-muted-foreground">We do not have a record for you yet.</p>
        </div>
      </MainLayout>
    );
  }

  const signedDocs = [
    { label: "Member Engagement Agreement", url: record.mea_document_url, signedAt: record.mea_signed_at },
    { label: "Suite lease", url: record.lease_document_url, signedAt: record.lease_signed_at },
    { label: "NDA", url: record.nda_document_url, signedAt: record.nda_signed_at },
  ].filter((d) => Boolean(d.url));

  return (

    <MainLayout>
      <div className="container max-w-3xl mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Your record</h1>
          <p className="text-muted-foreground text-sm">
            This is what we hold for you. Some parts lock once your papers are in motion, and we can still fix those for you.
          </p>
        </div>

        <Card>
          <CardHeader><CardTitle className="text-base">Identity</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <Row f={field("first_name", "First name", record.first_name)} />
            <Row f={field("last_name", "Last name", record.last_name)} />
            <Row f={field("legal_full_name", "Full legal name", record.legal_full_name)} />
            <Row f={field("email", "Email", record.email, false)} />
            <Row f={field("phone", "Phone", record.phone)} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Addresses</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <Row f={field("legal_address", "Legal address", addressText(record.legal_address), true, "address")} />
            <Row
              f={field(
                "shipping_address",
                "Shipping",
                hasSeparateShipping(record)
                  ? addressLines(shippingAddressOf(record)).join("\n")
                  : "Ship to my legal address.",
                false,
              )}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Household</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <Row f={field("home_situation", "Home", HOME_LABEL[record.home_situation] || "", false)} />
            <Row f={field("spouse_name", "Significant other", record.spouse_name)} />
            <Row f={field("adult_children_count", "Adult children", record.adult_children_count)} />
            <Row f={field("finance_segment", "Segment", FINANCE_LABEL[record.finance_segment] || "", false)} />
            <Row
              f={field(
                "chancery_interest",
                "Open to administration conversation",
                record.chancery_interest === true
                  ? `Yes${record.chancery_interest_at ? ` on ${new Date(record.chancery_interest_at).toLocaleDateString()}` : ""}`
                  : record.chancery_interest === false
                    ? "Not right now"
                    : "Not asked",
                false,
              )}
            />

          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Association</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <Row f={field("una_name", "Association name", record.una_name)} />
            <Row f={field("principal_officer_name", "Principal officer", record.principal_officer_is_self ? "Yourself" : record.principal_officer_name, !record.principal_officer_is_self)} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Suite and lease</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <Row f={field("suite_number", "Suite number", record.suite_number)} />
            <Row f={field("lease_status", "Lease", record.lease_status === "signed" ? "Signed" : record.lease_status === "sent" ? "Waiting for your signature" : "Not started", false)} />
            <Row f={field("payment_status", "Payment", record.payment_status === "complete" ? "Complete" : "Not started", false)} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Signed documents</CardTitle>
            <CardDescription>Copies of what you have signed with us.</CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            {signedDocs.length === 0 ? (
              <p className="text-sm text-muted-foreground">Your signed documents will appear here.</p>
            ) : (
              signedDocs.map((d) => (
                <div key={d.label} className="flex items-center justify-between gap-3 border-b py-3 last:border-b-0">
                  <div className="min-w-0">
                    <div className="text-sm">{d.label}</div>
                    <div className="text-xs text-muted-foreground">
                      {d.signedAt ? `Signed ${new Date(d.signedAt).toLocaleDateString()}` : "Signed"}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" asChild>
                    <a href={d.url} target="_blank" rel="noopener noreferrer">Open</a>
                  </Button>
                </div>
              ))
            )}
          </CardContent>
        </Card>


        <Card>
          <CardHeader>
            <CardTitle className="text-base">Formation</CardTitle>
            <CardDescription>
              <Badge variant="secondary">{STAGE_LABEL[record.fulfillment_stage] || record.fulfillment_stage}</Badge>
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <Row f={field("tracking_number", "Tracking number", record.tracking_number, false)} />
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!editField} onOpenChange={(o) => { if (!o) setEditField(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit {editField?.label.toLowerCase()}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="edit-value">{editField?.label}</Label>
            <Input id="edit-value" value={editValue} onChange={(e) => setEditValue(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditField(null)}>Cancel</Button>
            <Button onClick={() => void saveEdit()} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!addrField} onOpenChange={(o) => { if (!o) setAddrField(null); }}>
        <DialogContent className="max-h-[85dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit {addrField?.label.toLowerCase()}</DialogTitle>
            <DialogDescription>
              Enter it exactly as it appears on your driver's license.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <AddressFields value={addrValue} onChange={setAddrValue} idPrefix="record-addr" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddrField(null)}>Cancel</Button>
            <Button onClick={() => void saveAddress()} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!fixField} onOpenChange={(o) => { if (!o) setFixField(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request a correction</DialogTitle>
            <DialogDescription>
              This one is locked while your papers are in motion. Tell us what it should say and we will handle it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fix-value">What it should say</Label>
              <Input id="fix-value" value={fixValue} onChange={(e) => setFixValue(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fix-note">Anything else we should know</Label>
              <Textarea id="fix-note" rows={3} value={fixNote} onChange={(e) => setFixNote(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFixField(null)}>Cancel</Button>
            <Button onClick={() => void submitFix()} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Send request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
